import type {RefObject} from 'react';

import {useGridSettingsSafe} from '../store/selectors';
import {useEagGridStore} from '../store/useEagGridStore';
import type {EagColumn} from '../types/EagColumn';
import {getHeaderButtonWidth} from '../utils/getHeaderButtonWidth';
import {measureColumnContentWidth} from '../utils/measureColumnContentWidth';

type UseColumnAutosizeReturn = {
  autosizeColumn: (key: string) => void;
  autosizeAllColumns: () => void;
};

/**
 * Provides column auto-sizing callbacks that dynamically measure content width.
 *
 * Reads `headerHeight` from the grid settings store and accepts `showColumnMenu`
 * as a parameter to compute the exact header button region width — no hardcoded
 * padding constants.
 *
 * The measurement clones each cell into an offscreen container to read the
 * unconstrained `offsetWidth`, which already includes all CSS padding and
 * renderer-internal spacing (chips, flags, icons, etc.).
 *
 * @param tableRef - Ref to the rendered `<table>` element
 * @param flatColumnMap - Stable Map<key, EagColumn> for looking up per-column minWidth
 * @param showColumnMenu - Whether the column menu (sort + menu buttons) is visible in header cells
 */
export const useColumnAutosize = (
  tableRef: RefObject<HTMLTableElement | null>,
  flatColumnMap: Map<string, EagColumn>,
  showColumnMenu: boolean
): UseColumnAutosizeReturn => {
  const store = useEagGridStore();
  const gridSettings = useGridSettingsSafe();

  const headerButtonWidth = getHeaderButtonWidth(gridSettings.headerHeight, showColumnMenu);

  const measureKey = (table: HTMLTableElement, key: string) =>
    measureColumnContentWidth(table, key, {
      minWidth: flatColumnMap.get(key)?.minWidth,
      headerButtonWidth,
    });

  const autosizeColumn = (key: string) => {
    if (!tableRef.current) {
      return;
    }
    const width = measureKey(tableRef.current, key);
    if (width != null) {
      store.setColumnWidth(key, width);
    }
  };

  const autosizeAllColumns = () => {
    if (!tableRef.current) {
      return;
    }
    const table = tableRef.current;
    // Single pass over one columnStates snapshot. Hidden columns keep their early-return
    // so they stay unmeasured; visible columns get the measured width when measurable.
    store.setColumnStates(
      store.getState().columnStates.map((cs) => {
        if (cs.hidden === true) {
          return cs;
        }
        const width = measureKey(table, cs.key);
        return width != null ? {...cs, width} : cs;
      })
    );
  };

  return {autosizeColumn, autosizeAllColumns};
};
